/* eslint-disable react/jsx-one-expression-per-line */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { onReplaceLinkClick } from 'ctm-util-functions';
import { Icon, SimpleFlexbox } from 'ctm-components';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { useEverestConfig } from 'ctm-app/client/hooks/useEverestConfig';
import styles from './edit_car_details.css';

/**
 * Edit car detail list item component
 * @param {Object} props - the component's props object
 * @param {String} props.identifier - item's id, e.g. make/year/model
 * @param {String} props.value - item's text value
 * @param {String} props.splitTrafficId - splitTrafficId
 * @returns {Node} a link that click through to each car part page
 * @constructor
 */

const onClick = (identifier, event) => onReplaceLinkClick(event, `/car-insurance/journey/car_${identifier}`, 'isEdit=true');
const useStyles = createUseStyles({
    divider: {
        marginLeft: '0.25rem',
        marginRight: '0.25rem'
    },
    label: {
        marginRight: '0.5rem'
    },
    description: {
        fontWeight: '600'
    },
    removeMargin: {
        marginBottom: '0rem'
    },
    removeLeftMargin: {
        marginLeft: 'unset'
    },
    inlineCenter: {
        display: 'inline-flex',
        alignSelf: 'center'
    },
    flex: {
        display: 'flex'
    },
    hide: {
        display: 'none'
    },
    contents: {
        display: 'contents'
    },
    wrap: {
        display: 'inline-flex',
        flexWrap: 'wrap'
    },
    wordBreak: {
        wordBreak: 'break-all'
    },
    w95: {
        width: '95%'
    }
});

const EditCarDetailsItem = React.memo((props) => {
    const { identifier, value, splitTrafficId } = props;
    const classes = useStyles();
    const { brandcode } = useEverestConfig();

    // If passed in field is null, hide it
    const updateClassName = (field) => {
        return field === null || field === 'NO_BADGE' || field === '' ? classes.hide : null;
    };

    const getIdentifier = (id) => {
        if (id === 'series' && brandcode !== 'choo') {
            // Badge step not implemented for CHOOSI, therefore no re-direction for user
            return 'badge';
        }
        if (splitTrafficId === 'mvlTest2' && id === 'body') {
            return 'fuel';
        }
        if (splitTrafficId === 'mvlTest2' && id === 'transmission') {
            return 'fuel';
        }
        return id;
    };

    const renderCarSeriesDescriptions = () => {
        const descriptions = [
            { label: value.SERIES.label, description: value.SERIES.description },
            { label: value.BADGE.label, description: value.BADGE.description },
            { label: value.ENGINE.label, description: value.ENGINE.description },
            { label: value.TRANSMISSION.label, description: value.TRANSMISSION.description },
            { label: value.POWER.label, description: value.POWER.description },
            { label: value.DRIVE.label, description: value.DRIVE.description },
            { label: value.KERBMASS.label, description: value.KERBMASS.description },
            { label: value.RELEASE.label, description: value.RELEASE.description }
        ];

        return (
            <SimpleFlexbox row className={classes.removeLeftMargin}>
                <span className={classes.w95} data-id={'series-descriptions'}>
                    {descriptions.map((item, index) => (
                        <Fragment data-id={`series-descriptionFor_${item.label}`} key={item.label}>
                            <span id={`series-label-${item.label}`} className={classNames(updateClassName(item.description), classes.removeMargin, classes.label)}>
                                {item.label}:
                            </span>
                            <span
                                aria-labelledby={`series-label-${item.label}`}
                                id={`series-description_${item.label.trim()}`}
                                className={classNames(updateClassName(item.description), classes.removeMargin, classes.description)}
                            >
                                {item.description}
                            </span>
                            {index !== descriptions.length - 1 && <span className={classNames(updateClassName(item.description), classes.divider)}>|</span>}
                        </Fragment>
                    ))}
                </span>
            </SimpleFlexbox>
        );
    };

    return (
        <a
            className={classNames(styles.link, identifier === 'series' && classes.flex)}
            href="#"
            data-id={`edit_car_${identifier}`}
            onClick={onClick.bind(this, getIdentifier(identifier))}
        >
            <Icon className={classNames(styles.edit_icon, classes.inlineCenter)} variant="ctm_pencil" />
            {identifier === 'series' ? renderCarSeriesDescriptions() : <span className={styles.text}>{value}</span>}
        </a>
    );
});

EditCarDetailsItem.propTypes = {
    identifier: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.object.isRequired]),
    splitTrafficId: PropTypes.string
};

export { EditCarDetailsItem };
